import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import {
	Box,
	Button,
	Checkbox,
	Divider,
	FormControlLabel,
	FormGroup,
	InputAdornment,
	ListItemText,
	MenuItem,
	Select,
	type SelectChangeEvent,
	Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import type React from "react";
import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useRecommendationsContext } from "../../../../contexts/Recommendations/RecommendationsContext";
import { useGlobalManualLocationOrder } from "../../../../contexts/Staffing/GlobalManualLocationOrderContext";
import { useDebouncedValue } from "../../../../hooks/useDebouncedValue";
import { useJobs } from "../../../../hooks/useJobs";
import { useFeatureFlag } from "../../../../hooks/useOpenFeatureFlag";
import { INK, INK_BLACK, NEUTRAL } from "../../../../theme/tokens";
import { type NurseFullRole, type PostShiftDetails, type PostShiftExtraData, RecommendationType, RequestSource } from "../../../../utils/models";
import { sortByLocationOrder } from "../../../../utils/multiColumnSorting";
import { isOpenShiftDurationAllowed, MAX_OPEN_SHIFT_DURATION_HOURS } from "../../../../utils/openShiftDuration";
import { getRolesOfRoleGroups } from "../../../../utils/roles";
import { getUniqueLocationNames } from "../../../../utils/utilFunctions";
import { SystemSwitch } from "../../../UIToolkit/Clickable/SystemSwitch";
import { SystemDropdown } from "../../../UIToolkit/Inputs/SystemDropDown";
import { SystemValidationTextField } from "../../../UIToolkit/Inputs/SystemValidationTextField";
import { CommonHeader } from "../../../UIToolkit/molecules/CommonHeader";
import { ShiftDetailedTimeViewWithPicker } from "../../../UIToolkit/molecules/ShiftDetailedTimeViewWithPicker";
import { ShiftStats } from "../../../UIToolkit/molecules/ShiftStats";
import { SystemDatePickerPopup } from "../../../UIToolkit/Popups/SystemDatePickerPopup";
import { useCheckOverlappingShiftsAndPatch } from "../../hooks/useHandleCheckOverlappingShifts";

export const PostShift = () => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { dateString } = useParams();
	const { checkForOverlapsAndMergeIfUserApproved, postLoading, patchLoading } = useCheckOverlappingShiftsAndPatch();
	const { chosenDepartment, showErrorSnackbar, latestDateInWeeksData, isSnfDemo, isHospitalDemo, timezone, isMedicalDepartment } = useHospitalData();
	const availableJobs = useJobs();
	const { recommendations } = useRecommendationsContext();
	const isPickupByLocation = useFeatureFlag(FEATURE_FLAGS.PickupByLocation);
	const showLocationSelector = isPickupByLocation && !isMedicalDepartment;
	const { customStringOrderMap } = useGlobalManualLocationOrder();
	const availableLocations = useMemo(
		() => (showLocationSelector ? sortByLocationOrder(getUniqueLocationNames(chosenDepartment.locations), customStringOrderMap) : []),
		[showLocationSelector, chosenDepartment, customStringOrderMap],
	);
	const [selectedLocation, setSelectedLocation] = useState<string>("");
	const navigate = useNavigate();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const [shiftNo, setShiftNo] = useState<number>(0);
	const [availableShifts, setAvailableShifts] = useState<string>("");
	const [selectedRoleGroups, setSelectedRoleGroups] = useState<string[]>([]); // CLEANUP: remove after job certification rollout complete
	const [selectedJobId, setSelectedJobId] = useState<string>("");
	const selectedRoles = useMemo<NurseFullRole[]>(
		() => getRolesOfRoleGroups(selectedRoleGroups, chosenDepartment.role_groups),
		[selectedRoleGroups, chosenDepartment],
	);
	const date = useMemo<DateTime>(
		() => (dateString ? DateTime.fromISO(dateString) : DateTime.now()).setZone(chosenDepartment.timezone),
		[dateString, chosenDepartment.timezone],
	);

	const [shiftStart, setShiftStart] = useState<DateTime>(DateTime.now());
	const [shiftEnd, setShiftEnd] = useState<DateTime>(DateTime.now());

	const postShiftRecommendations = useMemo(() => {
		const rec = recommendations.find(
			(r) =>
				r.type === RecommendationType.PostShift &&
				r.date.hasSame(date, "day") &&
				r.extra_data &&
				Array.isArray((r.extra_data as PostShiftExtraData).data) &&
				(r.extra_data as PostShiftExtraData).data.length > 0,
		);
		return rec ? (rec.extra_data as PostShiftExtraData).data : [];
	}, [recommendations, date]);

	const isFromRecommendation = useMemo(() => {
		return postShiftRecommendations.some((rec) => {
			const recStart = DateTime.fromISO(rec.recommended_start_time).setZone(timezone);
			const recEnd = DateTime.fromISO(rec.recommended_end_time).setZone(timezone);
			const timesMatch = recStart.equals(shiftStart) && recEnd.equals(shiftEnd) && rec.recommended_staff_count.toString() === availableShifts;
			if (!timesMatch) return false;
			if (jobManagementEnabled && rec.recommended_job_id) {
				return rec.recommended_job_id === selectedJobId;
			}
			return rec.recommended_role_groups.every((rg) => selectedRoleGroups.includes(rg));
		});
	}, [postShiftRecommendations, shiftStart, shiftEnd, selectedRoleGroups, selectedJobId, availableShifts, timezone, jobManagementEnabled]);

	const handleUseRecommendation = useCallback(
		(recommendation: PostShiftDetails) => {
			if (jobManagementEnabled && recommendation.recommended_job_id) {
				setSelectedJobId(recommendation.recommended_job_id);
			} else if (recommendation.recommended_role_groups && recommendation.recommended_role_groups.length > 0) {
				setSelectedRoleGroups(recommendation.recommended_role_groups);
			}

			if (recommendation.recommended_staff_count && recommendation.recommended_staff_count > 0) {
				setAvailableShifts(recommendation.recommended_staff_count.toString());
			}

			if (recommendation.recommended_start_time && recommendation.recommended_end_time) {
				const startTime = DateTime.fromISO(recommendation.recommended_start_time).setZone(timezone);
				const endTime = DateTime.fromISO(recommendation.recommended_end_time).setZone(timezone);
				setShiftStart(startTime);
				setShiftEnd(endTime);
			}
		},
		[timezone, jobManagementEnabled],
	);

	const [extraPay, setExtraPay] = useState<boolean>(false);
	const [isExtraPayPercentage, setIsExtraPayPercentage] = useState<boolean>(false);
	const [extraPayAmount, setExtraPayAmount] = useState<number>(0);
	const [extraPayString, setExtraPayString] = useState<string>("");
	const [chargeOnly, setChargeOnly] = useState<boolean>(false);
	const isShiftBonusesEnabled = useFeatureFlag(FEATURE_FLAGS.EnableShiftBonuses);
	const toggleExtraPay = useCallback(() => {
		setExtraPay((prev) => !prev);
	}, []);
	const toggleExtraPayPercentage = useCallback(() => {
		setIsExtraPayPercentage((prev) => !prev);
	}, []);

	const toggleChargeOnly = useCallback(() => {
		setChargeOnly((prev) => !prev);
	}, []);

	const handleExtraPayAmountChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const value = event.target.value;
			let parsedValue = 0;
			if (value === "") {
				setExtraPayAmount(0);
				setExtraPayString("0");
			} else {
				parsedValue = Number.parseFloat(value);
				if (Number.isNaN(parsedValue) || parsedValue < 0) {
					setExtraPayString("0");
					return;
				}
				setExtraPayAmount(Number.parseFloat(parsedValue.toFixed(isExtraPayPercentage ? 0 : 2)));
			}
			setExtraPayString(!isExtraPayPercentage ? value : parsedValue.toFixed(0));
		},
		[isExtraPayPercentage],
	);

	const debouncedExtraPayAmount = useDebouncedValue(extraPayAmount, 700);

	useEffect(() => {
		setExtraPayString(debouncedExtraPayAmount.toFixed(isExtraPayPercentage ? 0 : 2));
	}, [debouncedExtraPayAmount, isExtraPayPercentage]);

	const handleRoleGroupChange = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		const values = Array.isArray(value) ? value : [value];

		if (values.at(-1) === "") {
			setSelectedRoleGroups([]);
			return;
		}

		setSelectedRoleGroups(values);
	};

	const createShift = async (): Promise<void> => {
		try {
			if (postLoading) return;
			if (!isOpenShiftDurationAllowed(shiftStart, shiftEnd)) {
				showErrorSnackbar(t("error.open.shift.duration.too.long"), { date }, new Error("Open shift duration is invalid"));
				return;
			}

			const neededNurses = Number.parseInt(availableShifts, 10);
			track("post_shift_mobile", {
				shiftDetails: JSON.stringify({
					roles: selectedRoles,
					needed_nurses: neededNurses,
					start_time: shiftStart,
					end_time: shiftEnd,
					department_id: chosenDepartment.department_id,
					shift_index: shiftNo,
					location: selectedLocation || undefined,
				}),
			});
			await checkForOverlapsAndMergeIfUserApproved({
				neededNurses,
				shiftStart,
				shiftEnd,
				selectedRoles,
				jobId: selectedJobId || undefined,
				bonus: extraPayAmount,
				chargeOnly: chargeOnly,
				location: selectedLocation || undefined,
				panel: "create-mobile",
				requestSource: isFromRecommendation ? RequestSource.PostShiftRecommendation : RequestSource.PostShift,
				actionsWhenMergeNotNeeded: {
					onSuccess: () => {
						const path = `/${chosenDepartment.department_id}/staffing/${dateString}`;
						navigate(path);
					},
				},
				actionAfterMerge: () => {
					const path = `/${chosenDepartment.department_id}/staffing/${dateString}`;
					navigate(path);
				},
			});
		} catch (e) {
			showErrorSnackbar("Failed to post shift", { date }, e);
			return;
		}
	};

	const isCreateDisabled = (): boolean => {
		const hasSelection = jobManagementEnabled ? !!selectedJobId : selectedRoles.length > 0;
		return postLoading || patchLoading || !hasSelection || !Number.parseInt(availableShifts) || !isOpenShiftDurationAllowed(shiftStart, shiftEnd);
	};

	const dateTextRef = useRef<HTMLElement | null>(null);
	const [pickerOpen, setPickerOpen] = useState<boolean>(false);
	const togglePicker = useCallback(() => {
		setPickerOpen((prevState) => !prevState);
	}, []);
	const handleDateChange = useCallback(
		(newDate: DateTime | null) => {
			if (newDate?.isValid) {
				const path = `/${chosenDepartment.department_id}/post-shift/${newDate.toFormat("yyyy-MM-dd")}`;
				navigate(path);
			}
		},
		[navigate, chosenDepartment],
	);
	const handleOnKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key === "Enter" || event.key === " ") {
				togglePicker();
			}
		},
		[togglePicker],
	);

	return (
		date && (
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					width: "100%",
					height: "calc(100% - 10px)",
					justifyContent: "space-between",
					bgcolor: "white",
					p: 2,
					borderRadius: 2,
					overflow: "auto",
					boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
				}}
			>
				<SystemDatePickerPopup
					open={pickerOpen}
					onClose={togglePicker}
					anchorEl={dateTextRef.current}
					value={date}
					format={"mm/dd/yyyy"}
					onChange={handleDateChange}
					overridePickerProps={{
						maxDate: latestDateInWeeksData,
					}}
				/>
				<CommonHeader
					title={t("post.shift")}
					endingContent={
						<Typography
							ref={dateTextRef}
							style={{ cursor: "pointer" }}
							onClick={togglePicker}
							tabIndex={0}
							onKeyDown={handleOnKeyDown}
							sx={{
								color: "primary.main",
								fontSize: "14px",
								border: `1px solid ${NEUTRAL[300]}`,
								borderRadius: "4px",
								p: 0.25,
							}}
						>
							{date.toFormat("MMM D, yyyy")}
						</Typography>
					}
					onClose={() => navigate(`/${chosenDepartment.department_id}/staffing/${dateString}`)}
				/>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						width: "100%",
						height: "100%",
						justifyContent: "space-between",
						overflow: "auto",
					}}
				>
					{postShiftRecommendations.length > 0 && (
						<Box sx={{ px: 2, py: 1 }}>
							<Typography sx={{ color: INK.PRIMARY, fontSize: "14px", fontWeight: 600, mb: 1 }}>{t("recommendations")}</Typography>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
								{postShiftRecommendations.map((rec) => (
									<Box
										key={rec.recommended_role_groups.join(",") + rec.recommended_start_time}
										onClick={() => handleUseRecommendation(rec)}
										sx={{
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											p: 1.5,
											border: `1px solid ${NEUTRAL[300]}`,
											borderRadius: "8px",
											cursor: "pointer",
											bgcolor: "white",
											transition: "all 0.2s",
											"&:hover": {
												bgcolor: NEUTRAL[100],
												borderColor: "primary.main",
											},
										}}
									>
										<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, flex: 1 }}>
											<Typography sx={{ fontSize: "13px", fontWeight: 500, color: INK.PRIMARY }}>
												{jobManagementEnabled && rec.recommended_job_id
													? (chosenDepartment.jobs?.find((j) => j.id === rec.recommended_job_id)?.displayName ?? "")
													: rec.recommended_role_groups?.map((rg) => rg.charAt(0).toUpperCase() + rg.slice(1)).join(", ")}{" "}
												• {DateTime.fromISO(rec.recommended_start_time).toFormat("HH:mm")} - {DateTime.fromISO(rec.recommended_end_time).toFormat("HH:mm")}
											</Typography>
										</Box>
									</Box>
								))}
							</Box>
						</Box>
					)}
					<Box sx={{ px: 1, py: 1, display: "flex", flexDirection: "column" }}>
						<ShiftStats date={date} shiftIndex={shiftNo} />
						<Box
							sx={{
								width: "100%",
								paddingBlock: 2,
							}}
						>
							<Divider sx={{ width: "100%" }} />
						</Box>
						<Box data-testid="post-shift-role" sx={{ mt: 1, mb: 0.5 }}>
							{jobManagementEnabled ? (
								<>
									<Typography sx={{ color: "primary.main", mb: 1, fontSize: "12px" }}>{t("job")}</Typography>
									<Select
										value={selectedJobId}
										onChange={(e) => setSelectedJobId(e.target.value as string)}
										displayEmpty
										fullWidth
										IconComponent={(props) => <KeyboardArrowDown {...props} />}
										sx={{
											height: "48px",
											bgcolor: "white",
											"& .MuiOutlinedInput-notchedOutline": { borderColor: NEUTRAL[300], borderRadius: "4px" },
											"&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
											"&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
										}}
										renderValue={(selected) => {
											if (!selected)
												return (
													<Box component="span" sx={{ color: "primary.main" }}>
														{t("select.job")}
													</Box>
												);
											const job = availableJobs.find((j) => j.id === selected);
											return job?.displayName || selected;
										}}
									>
										{availableJobs.map((job) => (
											<MenuItem key={job.id} value={job.id}>
												<ListItemText primary={job.displayName} />
											</MenuItem>
										))}
									</Select>
								</>
							) : (
								<>
									<Typography sx={{ color: "primary.main", mb: 1, fontSize: "12px" }}>{t("choose.roles")}</Typography>
									<Select
										multiple
										value={selectedRoleGroups}
										onChange={handleRoleGroupChange}
										displayEmpty
										fullWidth
										IconComponent={(props) => <KeyboardArrowDown {...props} />}
										sx={{
											height: "48px",
											bgcolor: "white",
											"& .MuiOutlinedInput-notchedOutline": { borderColor: NEUTRAL[300], borderRadius: "4px" },
											"&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
											"&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
										}}
										renderValue={(selected) => {
											if (!selected || selected.length === 0)
												return (
													<Box component="span" sx={{ color: "primary.main" }}>
														{t("select.roles")}
													</Box>
												);
											return selected.map((group) => group.charAt(0).toUpperCase() + group.slice(1)).join(", ");
										}}
									>
										<MenuItem value="">
											<em>{t("none")}</em>
										</MenuItem>
										{Object.entries(chosenDepartment.role_groups).map(([name, roles]) => (
											<MenuItem key={name} value={name}>
												<Checkbox checked={selectedRoleGroups.includes(name)} />
												<ListItemText primary={`${name.charAt(0).toUpperCase() + name.slice(1)} (${roles.join(", ")})`} />
											</MenuItem>
										))}
									</Select>
								</>
							)}
						</Box>

						<Box>
							<Typography sx={{ color: "primary.main", mb: 1, fontSize: "12px" }}>Available shifts Number</Typography>
							<Select
								value={availableShifts}
								onChange={(event: SelectChangeEvent<string>) => setAvailableShifts(event.target.value)}
								displayEmpty
								fullWidth
								IconComponent={(props) => <KeyboardArrowDown {...props} />}
								sx={{
									height: "48px",
									bgcolor: "white",
									"& .MuiOutlinedInput-notchedOutline": {
										borderColor: NEUTRAL[300],
										borderRadius: "4px",
									},
									"&:hover .MuiOutlinedInput-notchedOutline": {
										borderColor: "primary.main",
									},
									"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
										borderColor: "primary.main",
									},
								}}
								renderValue={(selected) => {
									if (!selected) {
										return (
											<Box component="span" sx={{ color: "primary.main" }}>
												#
											</Box>
										);
									}
									return `#${selected}`;
								}}
							>
								{[...Array(100)].map((_, i) => (
									<MenuItem key={i + 1} value={String(i + 1)}>
										{i + 1}
									</MenuItem>
								))}
							</Select>
						</Box>
						{showLocationSelector && (
							<Box sx={{ mt: 1, mb: 0.5 }}>
								<SystemDropdown
									label={t("pickup.by.location.select.location")}
									items={availableLocations}
									selectedItem={selectedLocation}
									onChange={(value) => setSelectedLocation(value ?? "")}
									clearable
									fullWidth
								/>
							</Box>
						)}
						<Box
							sx={{
								width: "100%",
								paddingBlock: 2,
							}}
						>
							<Divider sx={{ width: "100%" }} />
						</Box>
						<Box
							sx={{
								pt: 2,
							}}
						>
							<ShiftDetailedTimeViewWithPicker
								shiftIndex={shiftNo}
								setShiftIndex={setShiftNo}
								selectedRoleForShift={selectedRoles[0] as NurseFullRole}
								shiftStart={shiftStart}
								shiftEnd={shiftEnd}
								setShiftStart={setShiftStart}
								setShiftEnd={setShiftEnd}
								date={date}
								roleSource={"role"}
								selectedRoleGroups={selectedRoleGroups}
								enabled
								jobId={selectedJobId}
								maximumDurationHours={MAX_OPEN_SHIFT_DURATION_HOURS}
							/>
						</Box>

						{isShiftBonusesEnabled && (
							<Box
								sx={{
									pt: 2,
								}}
							>
								<FormGroup
									sx={{
										display: "flex",
										flexDirection: "column",
										gap: 2,
									}}
								>
									<FormControlLabel
										control={<Checkbox checked={extraPay} onChange={toggleExtraPay} />}
										label={t("incentive.pay")}
										sx={{
											m: 0,
											"& .MuiFormControlLabel-label": {
												fontSize: "14px",
												color: "primary.main",
											},
										}}
									/>
									<SystemSwitch label={t("charge.only")} checked={chargeOnly} onChange={toggleChargeOnly} />
									{extraPay && (
										<Box
											sx={{
												display: "flex",
												flexDirection: "column",
												gap: 2,
											}}
										>
											{(isHospitalDemo || isSnfDemo) && (
												<SystemSwitch label={t("by.percentage")} disabled={!extraPay} checked={isExtraPayPercentage} onChange={toggleExtraPayPercentage} />
											)}
											<SystemValidationTextField
												disabled={!extraPay}
												variant="outlined"
												reserveHeightForHelperText={false}
												label={isExtraPayPercentage ? t("percentage") : t("amount")}
												placeholder={isExtraPayPercentage ? "%" : "$"}
												inputType="number"
												value={extraPayString}
												onChange={handleExtraPayAmountChange}
												maxValue={1000}
												inputProps={{
													endAdornment: (
														<InputAdornment position="end">
															{isExtraPayPercentage ? <PercentOutlinedIcon fontSize="small" /> : <AttachMoneyOutlinedIcon fontSize="small" />}
														</InputAdornment>
													),
												}}
											/>
										</Box>
									)}
								</FormGroup>
							</Box>
						)}
					</Box>

					<Box
						sx={{
							display: "flex",
							gap: 2,
							position: "relative",
							zIndex: 1,
							bgcolor: "white",
							justifyContent: "flex-end",
							mt: 2,
							mr: 2,
						}}
					>
						<Button
							variant="outlined"
							fullWidth
							disabled={postLoading || patchLoading}
							onClick={() => navigate(`/${chosenDepartment.department_id}/staffing/${dateString}`)}
							sx={{
								color: "primary.main",
								borderColor: "primary.main",
								textTransform: "none",
								borderRadius: "4px",
								height: "40px",
								width: "95px",
								"&:hover": {
									borderColor: "primary.dark",
								},
							}}
						>
							{t("cancel")}
						</Button>
						<Button
							variant="contained"
							fullWidth
							disabled={isCreateDisabled()}
							onClick={createShift}
							sx={{
								bgcolor: "primary.main",
								color: "white",
								textTransform: "none",
								borderRadius: "4px",
								height: "40px",
								width: "95px",
								"&:hover": {
									bgcolor: "primary.dark",
								},
								"&.Mui-disabled": {
									bgcolor: "#EEEEF4",
									color: alpha(INK_BLACK, 0.38),
								},
							}}
						>
							{t("create")}
						</Button>
					</Box>
				</Box>
			</Box>
		)
	);
};
